import type { Asset } from '@schibsted-svp/svp-api-types';
import { formatPublishedDate } from '@vgtv/time-helpers';
import { Fragment, FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import { isLiveStream } from '../../../../utils/player/asset';
import { type i18n, useTranslation } from '../../../../utils/useTranslation';
import { ClickToPlay } from '../../components/ClickToPlay/ClickToPlay';
import { RichAssetTitle } from '../../components/RichAssetTitle/RichAssetTitle';
import { Typography } from '../../components/Typography/Typography';
import { UpcomingBadges } from '../../components/UpcomingBadges/UpcomingBadges';
import { useConfig } from '../../hooks/ConfigProvider';
import { useIcons } from '../../hooks/useIcons';
import { useTitleOverride } from '../../hooks/useTitleOverride';
import ns from '../../translations';
import {
  getRealAssetFlightTimesStart,
  isCurrentlyLive,
} from '../../utils/assetHelpers';

type Props = {
  asset?: Asset;
  isLoading?: boolean;
};

export const getTimestampLabel = (asset: Asset, i18n: i18n) => {
  const flightTimesStartInSeconds = getRealAssetFlightTimesStart(asset, false);

  if (!flightTimesStartInSeconds) {
    return undefined;
  }

  return formatPublishedDate(
    flightTimesStartInSeconds,
    i18n.locale === 'no' ? 'nb' : 'sv'
  );
};

export const IdleLayoutElements: FunctionComponent<Props> = ({
  asset,
  isLoading = false,
}) => {
  const { i18n } = useTranslation(ns);
  const { context, ui = {} } = useConfig();
  const { Bullet } = useIcons();
  const titleOverride = useTitleOverride(asset);

  if (!asset) {
    return null;
  }
  const isLive = asset && isLiveStream(asset);
  const timestamp = useMemo(
    () => getTimestampLabel(asset, i18n),
    [asset, i18n]
  );

  const { t } = useTranslation(ns);

  const isStarted = isCurrentlyLive(asset);

  const generateLabels = () => {
    if (isLive) {
      if (isStarted) {
        return (
          <div className="vgp-idle-layout-top">
            <Typography
              variant="label"
              size="medium"
              bold
              className="vgp-idle-live-badge"
              data-skin="breaking"
            >
              <Bullet />
              {t('LIVE')}
            </Typography>
          </div>
        );
      }
      return (
        <UpcomingBadges
          className="vgp-idle-layout-top"
          color="tag-text"
          label={timestamp}
        />
      );
    }
  };

  return (
    <Fragment>
      {generateLabels()}
      {isLoading && (
        <div className="vgp-idle-layout-content">
          <Typography variant="label" size="large" color="white">
            {t('Loading ...')}
          </Typography>
        </div>
      )}
      {asset && (
        <Fragment>
          <div className="vgp-idle-layout-shadow" />
          <div className="vgp-idle-layout-bottom">
            {context === 'embed' && !ui.hideTitle && (
              <RichAssetTitle asset={asset} titleOverride={titleOverride} />
            )}
            {!isLoading && <ClickToPlay className="vgp-idle-play-button" />}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};
